/**
 * FeedbackButtons Component
 * 
 * Displays thumbs up/down feedback buttons for assistant responses
 * Matches P2S branding with smooth animations and optional remarks field
 */

import React, { useState, useEffect } from 'react';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import { submitFeedback } from '../api/conversationApi';
import './FeedbackButtons.css';

const FeedbackButtons = ({ 
  messageId, 
  conversationId, 
  accessToken,
  existingFeedback = null,
  onFeedbackSubmit = null,
  prefixSlot = null
}) => {
  const [selectedRating, setSelectedRating] = useState(null);
  const [showRemarksField, setShowRemarksField] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Load existing feedback if available
  useEffect(() => {
    if (existingFeedback) {
      setSelectedRating(existingFeedback.rating);
      setRemarks(existingFeedback.remarks || '');
      setIsSubmitted(true);
    }
  }, [existingFeedback]);

  const handleThumbsClick = async (rating) => {
    if (isSubmitted || isSubmitting) {
      return; // Already submitted or submitting
    }

    setSelectedRating(rating);
    setErrorMessage(null);

    // If thumbs down, show remarks field
    if (rating === 'thumbs_down') {
      setShowRemarksField(true);
      // Don't submit yet - wait for user to optionally add remarks
      return;
    }

    // If thumbs up, submit immediately
    await submitFeedbackToBackend(rating, null);
  };

  const handleSubmitWithRemarks = async () => {
    if (isSubmitted || isSubmitting || !selectedRating) {
      return;
    }

    await submitFeedbackToBackend(selectedRating, remarks.trim() || null);
  };

  const submitFeedbackToBackend = async (rating, remarksText) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await submitFeedback(
        accessToken,
        conversationId,
        messageId,
        rating,
        remarksText
      );

      if (response.success) {
        setIsSubmitted(true);
        setShowRemarksField(false);
        
        // Call parent callback if provided
        if (onFeedbackSubmit) {
          onFeedbackSubmit(response.feedback);
        }
      } else {
        // Handle 409 conflict (already submitted)
        setErrorMessage(response.message || 'Feedback already submitted');
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      
      if (error.response && error.response.status === 409) {
        setErrorMessage('You have already submitted feedback for this response');
        setIsSubmitted(true);
      } else {
        setErrorMessage('Failed to submit feedback. Please try again.');
        setSelectedRating(null);
        setShowRemarksField(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  const handleCancelRemarks = () => {
    setShowRemarksField(false);
    setSelectedRating(null);
    setRemarks('');
  };

  return (
    <div className="feedback-container">
      <div className="feedback-buttons">
        {prefixSlot}
        <button
          className={`feedback-button ${selectedRating === 'thumbs_up' ? 'selected' : ''} ${isSubmitted ? 'disabled' : ''}`}
          onClick={() => handleThumbsClick('thumbs_up')}
          disabled={isSubmitted || isSubmitting}
          title={isSubmitted ? 'Feedback submitted' : 'This was helpful'}
          aria-label="Thumbs up"
        >
          <FaThumbsUp />
        </button>

        <button
          className={`feedback-button ${selectedRating === 'thumbs_down' ? 'selected' : ''} ${isSubmitted ? 'disabled' : ''}`}
          onClick={() => handleThumbsClick('thumbs_down')}
          disabled={isSubmitted || isSubmitting}
          title={isSubmitted ? 'Feedback submitted' : 'This could be improved'}
          aria-label="Thumbs down"
        >
          <FaThumbsDown />
        </button>
      </div>

      {/* Remarks field for thumbs down */}
      {showRemarksField && !isSubmitted && (
        <div className="feedback-remarks-container">
          <textarea
            className="feedback-remarks"
            placeholder="What could be improved? (optional)"
            value={remarks}
            onChange={handleRemarksChange}
            rows={3}
            maxLength={500}
          />
          <div className="feedback-remarks-actions">
            <button 
              className="feedback-submit-btn"
              onClick={handleSubmitWithRemarks}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
            <button 
              className="feedback-cancel-btn"
              onClick={handleCancelRemarks}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Success message */}
      {isSubmitted && !errorMessage && (
        <div className="feedback-success-message">
          Thank you for your feedback!
        </div>
      )}

      {/* Error message */}
      {errorMessage && (
        <div className="feedback-error-message">
          {errorMessage}
        </div>
      )}

      {/* Submitting indicator */}
      {isSubmitting && (
        <div className="feedback-submitting-message">
          Submitting...
        </div>
      )}
    </div>
  );
};

export default FeedbackButtons;
